import type { IAction } from '../../action/IAction'
import { ActionArgs } from '../../action/ActionArgs'
import { Field, ID, InputType, ObjectType } from 'type-graphql'

/**
 * A candidate email conversation that may duplicate an order/PO's canonical conversation, surfaced so an
 * agent can merge it into the right one. The email analog of ZendeskTicketDuplicateResult — it
 * describes a Chat conversation (the merge unit on the email channel) rather than an order/PO entity.
 */
@ObjectType()
export class EmailConversationDuplicateResult {
  /** Conversation (Chat) id. */
  @Field(() => ID)
  _id: string

  @Field(() => String, { nullable: true })
  subject?: string

  /** The conversation's sender (customer) email. */
  @Field(() => String, { nullable: true })
  senderEmail?: string

  /** The conversation's sender display-name. */
  @Field(() => String, { nullable: true })
  senderName?: string

  /** The conversation's latest-message time (lastMessageAt). */
  @Field(() => Date, { nullable: true })
  date?: Date

  @Field(() => ID, { nullable: true })
  orderId?: string

  @Field(() => ID, { nullable: true })
  purchaseOrderId?: string
}

/**
 * Args for detecting likely-duplicate email conversations for an order/PO — the email analog of
 * CheckZendeskTicketDuplicatesArgs. Candidates are matched by the customer's sender email and/or the
 * order/PO linkage (Chat.metadata.orderId / purchaseOrderId).
 */
@InputType()
export class CheckEmailDuplicatesArgs extends ActionArgs {
  /** Sender (customer) email to match candidate conversations against (case-insensitive). */
  @Field(() => String, { nullable: true })
  email?: string

  /** Order linkage — matches conversations stamped with this metadata.orderId. */
  @Field(() => ID, { nullable: true })
  orderId?: string

  /** Purchase-order linkage — matches conversations stamped with this metadata.purchaseOrderId. */
  @Field(() => ID, { nullable: true })
  purchaseOrderId?: string

  /** The canonical conversation to exclude, so it is never flagged as its own duplicate. */
  @Field(() => ID, { nullable: true })
  excludeChatId?: string
}

/**
 * Interface for the CheckEmailDuplicates action.
 * Returns an array of EmailConversationDuplicateResult — the candidate conversations available to merge.
 */
export interface ICheckEmailDuplicates extends IAction<EmailConversationDuplicateResult[], CheckEmailDuplicatesArgs> {}
